import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../utils/toast";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { Profile } from "../../../types/types";
import { formatText } from "../../../utils/common/formatUtils";
import SocialMediaIcon from "./socialMediaIcon";
import Field from "./field";
import ProfileIdentity from "./profileIdentity";

interface BasicDetailsProps {
  member: Profile;
  showCopyButton?: boolean;
  isOwnProfile?: boolean;
  reviewCount?: number;
}

const cardClass =
  "bg-white rounded-3xl p-4 shadow-[0_0_1px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.06),0_2px_4px_rgba(0,0,0,0.04)]";

const softBtn =
  "h-8 min-w-8 px-3 rounded-2xl bg-blue-50 text-blue-600 text-sm font-medium inline-flex items-center justify-center gap-1 hover:bg-blue-100 transition-colors";

const BasicDetails: React.FC<BasicDetailsProps> = ({
  member,
  showCopyButton = false,
  isOwnProfile = false,
  reviewCount = 0,
}) => {
  const { t } = useTranslation("publicProfile");

  const firstName = formatText(member.firstName) || "";
  const lastName = formatText(member.lastName) || "";
  const fullName = `${firstName} ${lastName}`.trim();
  const initials = `${member.firstName?.[0]?.toUpperCase() || ""}${
    member.lastName?.[0]?.toUpperCase() || ""
  }`;
  const hasImage = Boolean(member.profileImage?.trim());

  const socialLinks =
    member.fighter?.socialMediaLinks || member.manager?.socialMediaLinks || [];

  const handleShare = () => {
    const slug = `${firstName}-${lastName}`
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    const profileUrl = `${window.location.origin}/profile/${slug}?profileId=${member.id}`;
    navigator.clipboard
      .writeText(profileUrl)
      .then(() => toast.success(t("profile_copied_to_clipboard")))
      .catch(() => toast.error(t("failed_to_copy_profile")));
  };

  return (
    <div className="pb-2">
      <div data-profile-hero className="relative w-full h-80 lg:h-[520px] overflow-hidden bg-gradient-to-br from-zinc-800 to-black">
        {hasImage ? (
          <img
            src={member.profileImage}
            alt={fullName}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/10 text-[120px] lg:text-[200px] font-bold select-none">
            {initials}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/0 to-black/60" />

        <div className="absolute inset-x-0 bottom-0 p-4 lg:px-8 lg:py-6">
          <ProfileIdentity member={member} reviewCount={reviewCount} variant="hero" />
        </div>
      </div>

      <div className="px-4 lg:px-8 pt-4 lg:pt-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {showCopyButton && (
            <button type="button" onClick={handleShare} className={softBtn}>
              <ClipboardDocumentIcon className="w-4 h-4" />
              {t("copy_link", { defaultValue: "Copy link" })}
            </button>
          )}
        </div>
        <div className="flex gap-3 lg:gap-4">
          {socialLinks.map((link: any, index: any) => (
            <SocialMediaIcon
              key={index}
              type={link.socialMediaAccountType}
              url={link.socialMediaAccountUrl}
            />
          ))}
        </div>
      </div>

      {member.bio && (
        <p className="px-4 lg:px-8 pt-4 lg:pt-6 text-base text-gray-900 leading-6 text-left">
          {member.bio}
        </p>
      )}

      <div className="px-4 lg:px-8 pt-4 lg:pt-6">
        <div className={cardClass}>
          <div className="text-sm font-medium text-gray-900 mb-4 text-left">
            {t("basic_details")}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-start">
            <Field label="nationality" value={member.nationality} />
            <Field label="residence_country" value={member.residenceCountry} />
            {member.phone && <Field label="phone" value={member.phone} />}
            <Field label="profile_status" value={member.profileStatus} />
            <Field label="verified" value={member.verified ? "Yes" : "No"} />
            {member.gender && <Field label="gender" value={member.gender} />}
            {(member.profileType === "ORGANIZATION" || member.profileType === "MANAGER") && (
              <>
                {member.manager?.organizationName && (
                  <Field
                    label="organization_name"
                    value={member.manager.organizationName}
                  />
                )}
                {member.manager?.type && (
                  <Field
                    label="manager_type"
                    value={member.manager.type}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicDetails;